import { FC, useMemo } from 'react';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { EssentialInvoiceData, InvoiceByIdV2, JobData } from '../../invoices.model';
import { getStatusDisplayData } from '../../utils';
import { ChevronArrowRightIcon } from 'assets/icons';
import { RelatedEstimate } from 'containers/jobs/jobs.model';
import { If } from 'components';

interface InvoiceHeaderSectionProps {
  invoice: InvoiceByIdV2;
  onJobClick?: (jobId: string) => void;
  onEstimateClick?: (estimateId: string) => void;
}

const InvoiceHeaderSection: FC<InvoiceHeaderSectionProps> = ({ invoice, onJobClick, onEstimateClick }) => {
  // Extract essential data once at component level for better performance
  const essentialData = useMemo(
    (): EssentialInvoiceData => ({
      status: invoice.status,
      totalAmount: invoice.totalAmount,
      pendingAmount: invoice.pendingAmount || invoice.totalAmount,
      totalPaidAmount: invoice.totalPaidAmount || 0,
      totalPaidAfterRefund: invoice.totalPaidAfterRefund || 0,
      totalRefundedAmount: invoice.totalRefundedAmount || 0,
      statusIndicator: invoice.statusIndicator,
      tipAmount: invoice.tipAmount || 0,
      totalInvoicedAmount: invoice.totalInvoicedAmount || 0,
      closureAmount: invoice.closureAmount || 0
    }),
    [
      invoice.status,
      invoice.totalAmount,
      invoice.pendingAmount,
      invoice.totalPaidAmount,
      invoice.totalPaidAfterRefund,
      invoice.totalRefundedAmount,
      invoice.statusIndicator,
      invoice.totalInvoicedAmount,
      invoice.closureAmount
    ]
  );

  // Pass only essential data to utility function
  const statusData = useMemo(() => getStatusDisplayData(essentialData), [essentialData]);

  const renderPrimaryAmount = () => {
    const primary = statusData.primaryAmount;

    return (
      <>
        <div className="text-sbase font-medium text-accentText">{primary.label}</div>
        <div id="invoice-panel-total-amount" className="text-[30px] font-bold text-headingGray">
          {getAmountWithCurrency(primary.value)}
        </div>
      </>
    );
  };

  const renderStatusBadge = () => {
    const badge = statusData.statusBadge;

    return (
      <div
        id="invoice-panel-payment-status"
        className="rounded-md px-2.5 py-1 text-sbase font-semibold"
        style={{
          backgroundColor: badge.backgroundColor,
          color: badge.textColor
        }}>
        {badge.text}
      </div>
    );
  };

  const renderBreakdown = () => {
    if (!statusData.breakdown) return null;

    return (
      <div className="flex w-full justify-center gap-10 pt-2.5 text-sbase font-semibold">
        {statusData.breakdown.map((item, index) => (
          <div key={index} className="flex w-1/2 flex-col items-center gap-2 px-2.5">
            <div className="whitespace-nowrap text-accentText">{item.label}</div>
            <div className="flex items-center gap-2 whitespace-nowrap text-primaryText">
              {getAmountWithCurrency(item.value)}
              <If condition={!!item?.tipAmount}>
                <div>+ {getAmountWithCurrency(item.tipAmount)} Tip</div>
              </If>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderRelatedItem = (keyId: string, title: string, rightText: string, onClick: () => void) => (
    <div
      key={keyId}
      id={title.replaceAll(' ', '-')}
      className="-m-2 flex cursor-pointer items-center justify-center rounded-md p-2"
      onClick={onClick}
      tabIndex={0}>
      <span className="text-sbase font-semibold text-primaryText">{title}</span>
      <span className="px-1.5 text-[10px] text-accentText">•</span>
      <div className="flex items-center gap-2 text-accentText">
        <span className="text-sbase">{rightText}</span>
        <ChevronArrowRightIcon className="path-fill-current path h-4 w-4" />
      </div>
    </div>
  );

  const renderJobAndEstimateSection = () => {
    const hasRelatedJobs = invoice.relatedJobs && invoice.relatedJobs.length > 0;
    const hasRelatedEstimates = invoice.relatedEstimates && invoice.relatedEstimates.length > 0;

    if (!hasRelatedJobs && !hasRelatedEstimates) {
      return null;
    }

    return (
      <div className="space-y-2">
        {/* Related Jobs */}
        {hasRelatedJobs &&
          invoice.relatedJobs?.map(
            (job: JobData) =>
              renderRelatedItem(
                `job-${job.jobId}`,
                job.jobTitle || job.jobNumber || '',
                `Job ${job.jobStatus?.toLowerCase() || ''}`,
                () => onJobClick?.(job.jobId)
              )
            // eslint-disable-next-line function-paren-newline
          )}

        {/* Related Estimates */}
        {hasRelatedEstimates &&
          invoice.relatedEstimates?.map(
            (estimate: RelatedEstimate) =>
              renderRelatedItem(
                `estimate-${estimate.id}`,
                estimate.title || estimate.estimateNumber || '',
                `Estimate ${estimate.status.toLowerCase() || ''}`,
                () => onEstimateClick?.(estimate.id)
              )
            // eslint-disable-next-line function-paren-newline
          )}
      </div>
    );
  };

  return (
    <div className="rounded-md bg-white px-5 py-4 shadow-section">
      {/* Status Section */}
      <div className="flex flex-col items-center justify-center gap-1.5">
        {renderPrimaryAmount()}
        {renderStatusBadge()}
        {renderBreakdown()}
      </div>

      {/* Job & Estimate Section */}
      {((invoice.relatedJobs && invoice.relatedJobs.length > 0) ||
        (invoice.relatedEstimates && invoice.relatedEstimates.length > 0)) && (
        <div className="mt-3 border-t border-secondaryBtn pt-3">{renderJobAndEstimateSection()}</div>
      )}
    </div>
  );
};

export default InvoiceHeaderSection;
